import { ReactNode } from 'react';
import { TFunction } from 'i18next';

import { Translate } from 'components/Translate/Translate';

import {
  DocumentTypeWithVirtual,
  InvoiceItemDTO,
  MessageDTO,
  MessageV2DTO,
  ModificationDTO,
  RawTransactionDTO,
  ReceiverEnvelopeState,
  SenderEnvelopeDTO,
  SenderEnvelopeV2DTO,
  TransactionDTO,
  TransactionInternalType,
  TransactionState,
  ValueOf,
} from '../../types';
import { formatDayAndTime } from '../dateTime/dateTime';

const calulateAbsoluteItemDiscount = (
  modification: Pick<ModificationDTO, 'amount'>,
  item: Partial<Pick<InvoiceItemDTO, 'quantity'>>,
) => {
  return (modification.amount ?? 0) * (item.quantity ?? 0);
};

export const calculateLineItemTotal = (
  item: Partial<Pick<InvoiceItemDTO, 'quantity' | 'unitPrice' | 'modificationGroups' | 'isDepositItem'>>,
) => {
  const subTotal = (item.quantity ?? 0) * (item.unitPrice ?? 0);
  const firstModification = item.modificationGroups?.[0]?.modifications?.[0];
  const firstModificationIsRelative = typeof firstModification?.percentage !== 'undefined';

  if (!firstModification || !!item.isDepositItem) {
    return subTotal;
  }

  const modificationAmount = firstModificationIsRelative
    ? subTotal * ((firstModification.percentage ?? 0) / 100)
    : calulateAbsoluteItemDiscount(firstModification, item);

  return firstModification.type === 'ALLOWANCE' ? subTotal - modificationAmount : subTotal + modificationAmount;
};

export const checkMessageDropped = (message?: MessageDTO | MessageV2DTO) => {
  if (!message) {
    return false;
  }

  const receiverEnvelopeState = isMessageV2DTO(message)
    ? message?.receiver_envelope?.state
    : message.receiverEnvelope?.state;
  return message.state === TransactionState.dropped || receiverEnvelopeState === ReceiverEnvelopeState.dropped;
};

export const checkSenderEnvelopeDropped = (senderEnvelope?: SenderEnvelopeDTO | null) => {
  if (!senderEnvelope) {
    return false;
  }

  return senderEnvelope.state === TransactionState.dropped;
};

type DroppedErrorReason = MessageV2DTO['dropped_error_reasons'][number];

type FormattedDroppedReason = {
  displayMode: 'formatted' | 'fallback';
  header: string | null;
  content: ReactNode;
  howToFix?: string | null;
};

export const formatDroppedReason = (reason: DroppedErrorReason, t: TFunction): FormattedDroppedReason => {
  const isInternal = !!reason.dropped_by?.isAdmin;
  const userName = reason.dropped_by?.name;
  const partnerName = reason.dropped_by?.partner?.name;
  const droppedAt = reason.dropped_at;
  const reasonText = reason.reason || '';

  if (isInternal && droppedAt) {
    const formattedDate = formatDayAndTime(droppedAt);
    return {
      displayMode: 'formatted',
      header: null,
      content: (
        <Translate
          i18nKey="transactions:show.sections.errors.alert.droppedByProcuros"
          values={{
            date: formattedDate,
            reason: reasonText,
          }}
        />
      ),
      howToFix: null,
    };
  }

  if (!isInternal && userName && partnerName && droppedAt) {
    const formattedDate = formatDayAndTime(droppedAt);
    return {
      displayMode: 'formatted',
      header: null,
      content: (
        <Translate
          i18nKey="transactions:show.sections.errors.alert.droppedByUser"
          values={{
            userName,
            partnerName,
            date: formattedDate,
            reason: reasonText,
          }}
        />
      ),
      howToFix: null,
    };
  }

  return {
    displayMode: 'fallback',
    header: reason.title || t('transactions:show.sections.errors.alert.messageDropped'),
    content: reason.description || reasonText,
    howToFix: reason.howToFix || null,
  };
};

export const translationForDocumentTypeWithVirtual = (t: TFunction, type?: ValueOf<DocumentTypeWithVirtual> | null) => {
  if (Object.values(DocumentTypeWithVirtual).includes(type as DocumentTypeWithVirtual)) {
    return t(`common:messageTypes.singular.${type}`);
  }
  return t('common:messageTypes.singular.UNKNOWN');
};

export const isMessageV2DTO = (
  message: MessageDTO | MessageV2DTO | SenderEnvelopeDTO | SenderEnvelopeV2DTO | TransactionDTO,
): message is MessageV2DTO => {
  return 'receiver_envelope' in message;
};

export const determineInternalType = (transaction: RawTransactionDTO): TransactionInternalType => {
  if (transaction.messageId) {
    return TransactionInternalType.message;
  }

  return TransactionInternalType.senderEnvelope;
};

/**
 * i18n
 * t('common:messageTypes.singular.ACKNOWLEDGEMENT')
 * t('common:messageTypes.singular.CREDIT_NOTE')
 * t('common:messageTypes.singular.DEBIT_NOTE')
 * t('common:messageTypes.singular.DISPATCH_INSTRUCTION')
 * t('common:messageTypes.singular.DISPATCH_INSTRUCTION_RESPONSE')
 * t('common:messageTypes.singular.INVENTORY_REPORT')
 * t('common:messageTypes.singular.INVOICE')
 * t('common:messageTypes.singular.ORDER')
 * t('common:messageTypes.singular.ORDER_CHANGE')
 * t('common:messageTypes.singular.ORDER_CONFIRMATION')
 * t('common:messageTypes.singular.ORDER_REJECTION')
 * t('common:messageTypes.singular.ORDER_RESPONSE')
 * t('common:messageTypes.singular.PARTIAL_ORDER_CONFIRMATION')
 * t('common:messageTypes.singular.PRODUCT_CATALOG')
 * t('common:messageTypes.singular.QUOTE')
 * t('common:messageTypes.singular.QUOTE_REQUEST')
 * t('common:messageTypes.singular.RECEIVAL_NOTICE')
 * t('common:messageTypes.singular.REMITTANCE_ADVICE')
 * t('common:messageTypes.singular.SALES_REPORT')
 * t('common:messageTypes.singular.SERVICE_INVOICE')
 * t('common:messageTypes.singular.SHIPPING_NOTICE')
 * t('common:messageTypes.singular.UNKNOWN')
 *
 *
 *
 * t('common:messageTypes.plural.ACKNOWLEDGEMENT')
 * t('common:messageTypes.plural.CREDIT_NOTE')
 * t('common:messageTypes.plural.DEBIT_NOTE')
 * t('common:messageTypes.plural.DISPATCH_INSTRUCTION')
 * t('common:messageTypes.plural.DISPATCH_INSTRUCTION_RESPONSE')
 * t('common:messageTypes.plural.INVENTORY_REPORT')
 * t('common:messageTypes.plural.INVOICE')
 * t('common:messageTypes.plural.ORDER')
 * t('common:messageTypes.plural.ORDER_CHANGE')
 * t('common:messageTypes.plural.ORDER_CONFIRMATION')
 * t('common:messageTypes.plural.ORDER_REJECTION')
 * t('common:messageTypes.plural.ORDER_RESPONSE')
 * t('common:messageTypes.plural.PARTIAL_ORDER_CONFIRMATION')
 * t('common:messageTypes.plural.PRODUCT_CATALOG')
 * t('common:messageTypes.plural.QUOTE')
 * t('common:messageTypes.plural.QUOTE_REQUEST')
 * t('common:messageTypes.plural.RECEIVAL_NOTICE')
 * t('common:messageTypes.plural.REMITTANCE_ADVICE')
 * t('common:messageTypes.plural.SALES_REPORT')
 * t('common:messageTypes.plural.SERVICE_INVOICE')
 * t('common:messageTypes.plural.SHIPPING_NOTICE')
 * t('common:messageTypes.plural.UNKNOWN')
 */
